import React, { useEffect, useState } from 'react';
import {
  formatDateToShortDotV2,
  formatTime,
  getTimeColorClass,
  BaseButton,
  Toast,
  Modal,
} from '@repo/common';
import OptionList from './OptionList';
import { PAYMENT_COLUMN_CLASS as COL, ADMIN_CARD_CLASS } from '../paymentColumns';

import {
  cancelPayment,
  confirmBankTransfer,
  resendPaymentRequest,
} from '../../../../api/payment/payment.api';
import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-top.svg';

const formatMoney = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// 카드 결제는 기존 표기("15.00 $")를 유지하고, KRW 계좌이체는 "55,000 KRW" 로 보여준다
const formatMoneyWithCurrency = (value, currency) =>
  String(currency || '').toUpperCase() === 'KRW'
    ? `${Math.round(Number(value || 0)).toLocaleString('en-US')} KRW`
    : `${formatMoney(value)} $`;

const PaymentsRow = ({ item, onExpire }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showOptionList, setShowOptionList] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const [showSendSuccessToast, setShowSendSuccessToast] = useState(false);
  const [showCancelSuccessToast, setShowCancelSuccessToast] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  // 계좌이체: 주최측 입금 확인 후 어드민이 누르는 "이체 확인"
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConfirmSuccessToast, setShowConfirmSuccessToast] = useState(false);
  const [confirmProcessing, setConfirmProcessing] = useState(false);
  const isBankTransfer =
    String(item.paymentProvider || '').toUpperCase() === 'BANK_TRANSFER';

  const [timeLeft, setTimeLeft] = useState(() => {
    if (!item.expiresAt) return 0;

    const expireTime = new Date(item.expiresAt).getTime();
    const nowTime = new Date().getTime();

    const diffSeconds = Math.floor((expireTime - nowTime) / 1000);
    return diffSeconds > 0 ? diffSeconds : 0;
  });

  const isPending = item.status === 'HOLD' || item.status === 'OFFERED';

  const workshopTitleText = Array.isArray(item.workshopTitle)
    ? item.workshopTitle.map((title) => `[Workshop] ${title}`).join(', ')
    : `[Workshop] ${item.workshopTitle}`;

  useEffect(() => {
    if (!isPending || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (onExpire) onExpire();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isPending, timeLeft, onExpire]);

  const totalAmount = Array.isArray(item.amounts)
    ? item.amounts.reduce((sum, v) => sum + Number(v || 0), 0)
    : Number(item.amount || 0);

  const formattedAmount = formatMoneyWithCurrency(totalAmount, item.currency);

  const handleConfirmBankTransfer = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmBankTransferConfirm = async () => {
    if (confirmProcessing) return;
    setConfirmProcessing(true);
    try {
      await confirmBankTransfer({
        invoiceNumber: item.invoiceNumber,
        paymentId: item.paymentId,
      });
      setShowConfirmModal(false);
      setShowConfirmSuccessToast(false);
      setTimeout(() => setShowConfirmSuccessToast(true), 10);
      onExpire?.();
    } catch (err) {
      console.error('Failed to confirm bank transfer:', err);
      // 서버 에러 응답은 { code, msg, data } 형식 — msg 를 먼저 본다
      alert(
        err?.response?.data?.msg ||
          err?.response?.data?.message ||
          `Could not confirm the transfer (HTTP ${err?.response?.status ?? '?'}). Please try again.`,
      );
    } finally {
      setConfirmProcessing(false);
    }
  };

  const handleContact = () => {
    if (!item.userEmail) return;
    window.open(`mailto:${item.userEmail}`, '_blank');
  };

  const handleSendPaymentRequest = async () => {
    try {
      await resendPaymentRequest(item.bookingIds);
      setShowSendSuccessToast(false);
      setTimeout(() => setShowSendSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to send payment request:', err);
    }
  };

  const handleCancelPayment = () => {
    setShowCancelModal(true);
  };

  const handleCancelPaymentConfirm = async () => {
    try {
      const ids = selectedBookingId ? [selectedBookingId] : item.bookingIds;
      await cancelPayment(ids);
      setShowCancelSuccessToast(false);
      setTimeout(() => setShowCancelSuccessToast(true), 10);
      setShowCancelModal(false);
      onExpire?.();
    } catch (err) {
      console.error('Failed to cancel payment:', err);
    }
  };

  // bookingId 가 null 이면 인보이스 전체, 아니면 그 항목 하나에 대한 메뉴
  const optionButton = (bookingId) => (
    <button
      type='button'
      aria-label='Payment options'
      className='text-icon-placeholder hover:text-icon-tertiary active:text-icon-tertiary'
      onClick={(e) => {
        e.stopPropagation();
        setAnchorRect(e.currentTarget.getBoundingClientRect());
        setSelectedBookingId(bookingId);
        setShowOptionList(true);
      }}
    >
      <OptionIcon />
    </button>
  );

  const toggleButton = (
    <button
      type='button'
      aria-label={isOpen ? 'Collapse' : 'Expand'}
      className='text-icon-placeholder'
      onClick={() => setIsOpen((prev) => !prev)}
    >
      {isOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
    </button>
  );

  return (
    <div className='w-full'>
      {/* 표 — tabletV(1180px) 이상 */}
      <div
        className={`hidden tabletV:flex flex-col w-full items-start ${
          isOpen ? 'py-1 gap-2 bg-surface-lightgray60' : ''
        }`}
      >
        <div className={`flex w-full h-[48px] items-center ${!isOpen ? 'bg-grayscale-gray00' : ''}`}>
          <div className={`flex justify-center items-center p-1 ${COL.date}`}>
            <span className='typo-b14 text-text-secondary block w-full text-center truncate'>
              {formatDateToShortDotV2(item.pendingStartedAt)}
            </span>
          </div>
          <div className={`flex justify-center items-center p-1 ${COL.invoice}`}>
            <span className='typo-b14 text-text-secondary truncate'>{item.invoiceNumber}</span>
          </div>
          <div className={`flex flex-col justify-center items-center p-1 min-w-0 ${COL.item}`}>
            <span className='typo-b14Strong text-text-primary truncate block w-full'>
              {workshopTitleText}
            </span>
            {isBankTransfer && (
              <span className='typo-b11 text-text-placeholder'>Bank transfer</span>
            )}
          </div>
          <div className={`flex justify-center items-center p-1 ${COL.user}`}>
            <span className='typo-b14 text-text-secondary truncate block w-full text-center'>
              {item.userName}
            </span>
          </div>
          <div className={`flex justify-center items-center p-1 ${COL.amount}`}>
            <span className='typo-b14 text-text-secondary'>{formattedAmount}</span>
          </div>
          <div className={`flex flex-col justify-center items-center p-1 typo-b14 text-text-secondary ${COL.status}`}>
            <span className={`${getTimeColorClass(timeLeft)}`}>{formatTime(timeLeft)}</span>
          </div>
          <div className={`flex justify-center items-center p-1 ${COL.contact}`}>
            <BaseButton type='text_basic' size='s' onClick={handleContact} width='67px' hideIcon>
              Contact
            </BaseButton>
          </div>
          <div className={`flex justify-center items-center ${COL.icon}`}>{optionButton(null)}</div>
          <div className={`flex justify-start items-center ${COL.icon}`}>{toggleButton}</div>
        </div>

        {isOpen && (
          <div className='w-full flex flex-col gap-2'>
            {item.bookingIds.map((id, index) => (
              <div key={id} className='flex h-12 items-center typo-b14 text-text-secondary'>
                <span className={`flex p-1 ${COL.date}`} />
                <span className={`flex items-center justify-center p-1 gap-1 ${COL.invoice}`}>
                  <span className='text-text-placeholder'>Item ID</span>
                  <span>#{String(index + 1).padStart(2, '0')}</span>
                </span>
                <div className={`flex items-center justify-center p-1 gap-2.5 ${COL.item}`}>
                  <span className='truncate block w-full typo-b14Strong text-text-primary'>
                    [Workshop] {item.workshopTitle?.[index]}
                  </span>
                </div>
                <span className={`flex items-center justify-center p-1 gap-2.5 ${COL.user}`}>
                  <span className='truncate block w-full text-center'>{item.userName}</span>
                </span>
                <span className={`flex items-center justify-center p-1 gap-2.5 truncate ${COL.amount}`}>
                  {formatMoneyWithCurrency(item.amounts?.[index], item.currency)}
                </span>
                <span className={`flex p-1 ${COL.status}`} />
                <span className={`flex p-1 ${COL.contact}`} />
                <div className={`flex justify-center items-center ${COL.icon}`}>{optionButton(id)}</div>
                <span className={`flex ${COL.icon}`} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 카드 — tabletV 미만 */}
      <div className={ADMIN_CARD_CLASS}>
        <div className='flex items-start justify-between gap-2'>
          <div className='flex flex-col min-w-0 gap-0.5'>
            <span className='typo-b14Strong text-text-primary line-clamp-2'>{workshopTitleText}</span>
            <span className='typo-b12 text-text-secondary truncate'>
              {item.invoiceNumber} · {item.userName}
              {isBankTransfer ? ' · Bank transfer' : ''}
            </span>
          </div>
          <div className='flex-shrink-0 -mr-1'>{optionButton(null)}</div>
        </div>
        <div className='flex flex-wrap items-center gap-x-3 gap-y-1 typo-b13 text-text-secondary'>
          <span>{formatDateToShortDotV2(item.pendingStartedAt)}</span>
          <span className='typo-b13Strong text-text-primary'>{formattedAmount}</span>
          <span className={getTimeColorClass(timeLeft)}>{formatTime(timeLeft)}</span>
        </div>
        <div className='flex items-center justify-between'>
          <BaseButton type='text_basic' size='s' onClick={handleContact} hideIcon>
            Contact
          </BaseButton>
          <button
            type='button'
            className='flex items-center gap-1 typo-b13 text-text-secondary'
            onClick={() => setIsOpen((prev) => !prev)}
          >
            {item.bookingIds.length} {item.bookingIds.length === 1 ? 'item' : 'items'}
            {isOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
          </button>
        </div>
        {isOpen && (
          <div className='flex flex-col gap-2 pt-2 border-t border-grayscale-gray02'>
            {item.bookingIds.map((id, index) => (
              <div key={id} className='flex items-center gap-2 typo-b13 text-text-secondary'>
                <span className='flex-shrink-0 text-text-placeholder'>
                  #{String(index + 1).padStart(2, '0')}
                </span>
                <span className='flex-1 min-w-0 truncate typo-b13Strong text-text-primary'>
                  [Workshop] {item.workshopTitle?.[index]}
                </span>
                <span className='flex-shrink-0'>
                  {formatMoneyWithCurrency(item.amounts?.[index], item.currency)}
                </span>
                <span className='flex-shrink-0'>{optionButton(id)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {showOptionList && (
        <OptionList
          anchorRect={anchorRect}
          onClose={() => setShowOptionList(false)}
          slotId={item.slotId}
          status={item.status}
          paymentProvider={item.paymentProvider}
          hasPayment={item.paymentId != null}
          onSendPaymentRequest={handleSendPaymentRequest}
          onCanclePayment={handleCancelPayment}
          onConfirmBankTransfer={handleConfirmBankTransfer}
        />
      )}

      {showConfirmSuccessToast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <div className='w-full max-w-[320px] tabletH:w-[320px]'>
            <Toast
              title={'Transfer confirmed. Booking confirmed.'}
              hierarchy='important'
              direction='up'
              customWidth='100%'
            />
          </div>
        </div>
      )}

      {showConfirmModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => setShowConfirmModal(false)}
          onClickRightBtn={handleConfirmBankTransferConfirm}
          title={'Confirm bank transfer?'}
          subText={`Confirm only after the organizer has verified the deposit of ${formattedAmount} for ${item.invoiceNumber}.
The booking will be marked as confirmed and a confirmation email will be sent to the guest.`}
          buttonSize='xl'
          leftButtonLabel='Not yet'
          leftHideIcon={true}
          rightButtonLabel='Confirm'
          rightHideIcon={true}
          type='default'
        />
      )}

      {showSendSuccessToast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <div className='w-full max-w-[320px] tabletH:w-[320px]'>
            <Toast
              title={'Payment request email sent.'}
              hierarchy='important'
              direction='up'
              customWidth='100%'
            />
          </div>
        </div>
      )}

      {showCancelSuccessToast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <div className='w-full max-w-[232px] tabletH:w-[232px]'>
            <Toast
              title={'Payment canceled.'}
              hierarchy='important'
              direction='up'
              customWidth='100%'
            />
          </div>
        </div>
      )}

      {showCancelModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => {
            setShowCancelModal(false);
          }}
          onClickRightBtn={handleCancelPaymentConfirm}
          title={'Cancel payment?'}
          subText={`This will cancel the payment and void
the invoice. Any associated bookings will be canceled. This action cannot be undone.`}
          buttonSize='xl'
          leftButtonLabel='Keep'
          leftHideIcon={true}
          rightButtonLabel='Cancel'
          rightHideIcon={true}
          type='warning'
        />
      )}
    </div>
  );
};

export default PaymentsRow;
